import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Building2, Loader2, MessageSquare, RefreshCw, TrendingUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { usageApi } from "@/apis";
import type { UsageAppItem } from "@/apis";

interface MetricCardProps {
  label: string;
  value: number | string;
  icon: React.ReactNode;
}

function MetricCard({ label, value, icon }: MetricCardProps) {
  return (
    <div className="rounded-lg border bg-background p-3">
      <div className="flex items-center gap-2">
        <div className="text-muted-foreground">{icon}</div>
        <div>
          <p className="text-xs text-muted-foreground">{label}</p>
          <p className="text-lg font-semibold">{value}</p>
        </div>
      </div>
    </div>
  );
}

export function UsageDashboard() {
  const [apps, setApps] = useState<UsageAppItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const loadApps = async () => {
    setLoading(true);
    setError("");
    try {
      const data = await usageApi.listApps();
      setApps(data.items ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load usage data");
      setApps([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadApps();
  }, []);

  const getMetricValue = (app: UsageAppItem, key: string): number | string => {
    const metrics = app.summary?.metrics;
    if (metrics && typeof metrics === "object" && key in metrics) {
      const val = (metrics as Record<string, unknown>)[key];
      return typeof val === "number" ? val : String(val ?? "-");
    }
    // Fallback to legacy fields
    if (key === "companies_ingested") return app.summary?.companies_ingested ?? 0;
    if (key === "simulations_run") return app.summary?.simulations_run ?? 0;
    if (key === "active_users") return app.summary?.active_users ?? 0;
    return "-";
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-xl">Applications with Usage Enabled</CardTitle>
        <Button variant="outline" onClick={() => void loadApps()} disabled={loading}>
          <RefreshCw className={`mr-2 h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </CardHeader>
      <CardContent className="space-y-4">
        {error ? (
          <div className="rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-700">
            {error}
          </div>
        ) : null}

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : apps.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">
            No applications have usage tracking enabled. Enable it in AI Apps settings.
          </p>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            {apps.map((app) => {
              return (
                <Link
                  key={app.id}
                  to={`/settings/usage/${app.id}`}
                  className="group rounded-xl border border-primary/20 bg-primary/5 p-4 transition-all hover:bg-primary/10 hover:shadow-sm"
                >
                  <div className="flex items-start gap-3">
                    {app.icon ? (
                      <img
                        src={app.icon}
                        alt={`${app.appName} logo`}
                        className="h-10 w-10 shrink-0 rounded-md object-cover"
                      />
                    ) : (
                      <span className="mt-2 h-2.5 w-2.5 shrink-0 rounded-full bg-primary/80" />
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className="truncate font-semibold text-black">{app.appName}</h3>
                      </div>
                      {app.description ? (
                        <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">
                          {app.description}
                        </p>
                      ) : null}
                      {app.usage_error ? (
                        <p className="mt-2 text-xs text-amber-700">{app.usage_error}</p>
                      ) : null}
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
